import { FC, useEffect, useState } from 'react'
import useCountDown from 'react-countdown-hook'
import { Trans, useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'

import moment from 'moment'

import { Alert, Button, Drawer, Theme } from '@mui/material'
import makeStyles from '@mui/styles/makeStyles'
import withStyles, { ClassNameMap } from '@mui/styles/withStyles'

import { Spinner } from 'components/core/Progress/Progress-component'
import { useAppSelector } from 'redux/hooks'
import { logout, startRefreshToken } from 'redux/user/user-actions'
import { themeStyles, useTheme } from 'themes'
import { getRefreshToken } from 'utils/accessHelpers'

const styles = makeStyles<Theme>(theme => ({
    MuiDrawer: {
        zIndex: 9999,
    },
    alertRoot: {
        alignItems: 'center',

        [theme.breakpoints.down('sm')]: {
            alignItems: 'unset',
        },
    },
    message: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',

        [theme.breakpoints.down('sm')]: {
            flexDirection: 'column',
            alignItems: 'flex-start',
        },

        '& .sesionAlertText': {
            width: '100%',
            marginRight: 10,

            [theme.breakpoints.down('sm')]: {
                marginRight: 0,
                marginBottom: 10,
            },
        },
    },

    continueButton: {
        minWidth: 140,
        whiteSpace: 'nowrap',
        fontSize: 13,

        [theme.breakpoints.down('sm')]: {
            minWidth: 120,
        },

        '&:hover, &:active': {
            backgroundColor: 'rgba(141,99,29,0.10)',
        },
    },
}))

interface IProps {
    classes: ClassNameMap
}

const SessionTimeoutComponent: FC<IProps> = ({ classes }) => {
    const dispatch = useDispatch()
    const { loader: userLoader, currentUser } = useAppSelector(state => state.user)
    const { t } = useTranslation()
    const style = styles()
    const { formTheme } = useTheme()
    const refreshToken = getRefreshToken()
    const showAlertTimeLeft = 1000 * 120
    const [timeLeft, { start, reset }] = useCountDown(0, 1000)
    const [stateAlert, setStateAlert] = useState(false)
    const timeLeftSecods = timeLeft / 1000

    // console.log(`${moment(timeLeft).minutes()}`.padStart(2, '0'), ':', `${moment(timeLeft).seconds()}`.padStart(2, '0'))

    useEffect(() => {
        const refreshEndTime = localStorage.getItem('refreshEndTime')

        if (refreshEndTime) {
            const nowDate = new Date().getTime()
            const endDate = Date.parse(refreshEndTime)
            const newTime = endDate - nowDate

            start(newTime)
        }
    }, [])

    useEffect(() => {
        function activeEstateList() {
            const refreshEndTime = localStorage.getItem('refreshEndTime')
            const nowDate = new Date().getTime()
            const endDate = Date.parse(refreshEndTime)
            const newTime = endDate - nowDate

            !!refreshEndTime && start(newTime)
        }
        window.addEventListener('accessTokensUpdated', activeEstateList)

        return () => {
            window.removeEventListener('accessTokensUpdated', activeEstateList)
        }
    }, [])

    useEffect(() => {
        if (refreshToken !== 'null' && !!refreshToken) {
            if (!stateAlert && timeLeft < showAlertTimeLeft) {
                setStateAlert(true)
            }

            if (!!stateAlert && timeLeft > showAlertTimeLeft) {
                setStateAlert(false)
            }

            if (!!stateAlert && timeLeft <= 1) {
                dispatch(logout())
            }
        }
    }, [timeLeft, stateAlert, refreshToken, showAlertTimeLeft])

    const handleToggleAlert = () => {
        dispatch(startRefreshToken())
    }

    const timer = `${moment(timeLeft).minutes().toString().padStart(2, '0')}:${moment(timeLeft).seconds().toString().padStart(2, '0')}`

    if (!!currentUser && refreshToken !== 'null' && !!refreshToken && timeLeft > 0) {
        return (
            <Drawer anchor="top" open={stateAlert} classes={{ root: style.MuiDrawer }}>
                <Alert severity="warning" classes={{ root: style.alertRoot }}>
                    <div className={style.message}>
                        <div className="sesionAlertText">
                            <Trans i18nKey="SessionTimeout.AlertText">
                                Your session will expire in
                                <strong>{{ timer }}</strong>. Do you want to stay signed in?
                            </Trans>
                        </div>
                        <Button
                            className={style.continueButton}
                            color="secondary"
                            variant="outlined"
                            size="small"
                            onClick={handleToggleAlert}
                            disabled={!!userLoader?.REFRESH_TOKEN}
                        >
                            {userLoader?.REFRESH_TOKEN ? <Spinner color="#222222" size={20} /> : 'Stay signed in'}
                        </Button>
                    </div>
                </Alert>
            </Drawer>
        )
    }

    return null
}

export default withStyles(themeStyles.useSettingsPageStyles)(SessionTimeoutComponent)
